import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { fileUrl } from "@/lib/api";
import { Play, Pause, Heart, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";

export default function BeatCard({ beat, onChange, showCoverGen = false }) {
  const { play, track, playing } = usePlayer();
  const { user } = useAuth();
  const [genLoading, setGenLoading] = useState(false);

  const isActive = track && track.id === beat.id;
  const isPlaying = isActive && playing;
  const hasVoted = user && typeof user === "object" && beat.voters?.includes(user.id);

  const handleVote = async () => {
    if (!user || typeof user !== "object") {
      toast.error("Login to vote");
      return;
    }
    try {
      const { data } = await api.post(`/beats/${beat.id}/vote`);
      onChange && onChange(data);
    } catch (e) {
      toast.error("Vote failed");
    }
  };

  const handleGenCover = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post(`/beats/${beat.id}/cover`);
      onChange && onChange(data);
      toast.success("Cover art generated");
    } catch (e) {
      toast.error("Generation failed");
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div data-testid={`beat-card-${beat.id}`} className="border border-border bg-card group hover:border-primary transition-colors">
      <div className="aspect-square bg-muted relative overflow-hidden">
        {beat.cover_path ? (
          <img src={fileUrl(beat.cover_path)} alt={beat.title} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex">
              <span className="eq-bar" /><span className="eq-bar" /><span className="eq-bar" /><span className="eq-bar" />
            </div>
          </div>
        )}
        <button
          data-testid={`play-beat-${beat.id}`}
          onClick={() => play(beat)}
          className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          <span className="w-14 h-14 border-2 border-white bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </span>
        </button>
        {isPlaying && (
          <div className="absolute top-3 left-3 font-mono-acc text-[10px] flex items-center gap-2 bg-background/80 px-2 py-1 border border-primary text-primary">
            <span className="rec-dot" /> LIVE
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-display text-xl truncate" data-testid={`beat-title-${beat.id}`}>{beat.title}</p>
            <p className="font-mono-acc text-[10px] text-muted-foreground mt-1">@{beat.username}</p>
          </div>
          <button
            data-testid={`vote-beat-${beat.id}`}
            onClick={handleVote}
            className={`flex items-center gap-1 px-3 py-2 border ${
              hasVoted ? "border-primary bg-primary text-white" : "border-border hover:border-primary"
            }`}
          >
            <Heart size={14} fill={hasVoted ? "currentColor" : "none"} />
            <span className="font-mono-acc text-xs" data-testid={`beat-votes-${beat.id}`}>{beat.votes}</span>
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 font-mono-acc text-[10px] text-muted-foreground">
          {beat.bpm && <span className="border border-border px-2 py-1">{beat.bpm} BPM</span>}
          {beat.musical_key && <span className="border border-border px-2 py-1">{beat.musical_key}</span>}
          {beat.genre && <span className="border border-secondary text-secondary px-2 py-1">{beat.genre}</span>}
        </div>
        {showCoverGen && user && typeof user === "object" && beat.user_id === user.id && !beat.cover_path && (
          <button
            data-testid={`gen-cover-${beat.id}`}
            disabled={genLoading}
            onClick={handleGenCover}
            className="mt-3 w-full border border-secondary text-secondary font-mono-acc text-[10px] py-2 hover:bg-secondary hover:text-background flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles size={12} />
            {genLoading ? "Generating..." : "Generate AI Cover"}
          </button>
        )}
      </div>
    </div>
  );
}
